import { useState, useCallback, useEffect } from 'react'
import type { Editor } from '@tiptap/react'
import {
  Pilcrow,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  ChevronsUpDown,
  Check,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

interface BlockType {
  id: string
  label: string
  icon: typeof Pilcrow
  isActive: (editor: Editor) => boolean
  command: (editor: Editor) => void
}

const blockTypes: BlockType[] = [
  {
    id: 'paragraph',
    label: 'Text',
    icon: Pilcrow,
    isActive: (editor) =>
      editor.isActive('paragraph') &&
      !editor.isActive('bulletList') &&
      !editor.isActive('orderedList') &&
      !editor.isActive('blockquote'),
    command: (editor) => editor.chain().focus().setParagraph().run(),
  },
  {
    id: 'heading1',
    label: 'Heading 1',
    icon: Heading1,
    isActive: (editor) => editor.isActive('heading', { level: 1 }),
    command: (editor) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
  },
  {
    id: 'heading2',
    label: 'Heading 2',
    icon: Heading2,
    isActive: (editor) => editor.isActive('heading', { level: 2 }),
    command: (editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
  },
  {
    id: 'heading3',
    label: 'Heading 3',
    icon: Heading3,
    isActive: (editor) => editor.isActive('heading', { level: 3 }),
    command: (editor) => editor.chain().focus().toggleHeading({ level: 3 }).run(),
  },
  {
    id: 'bulletList',
    label: 'Bulleted List',
    icon: List,
    isActive: (editor) => editor.isActive('bulletList'),
    command: (editor) => editor.chain().focus().toggleBulletList().run(),
  },
  {
    id: 'orderedList',
    label: 'Numbered List',
    icon: ListOrdered,
    isActive: (editor) => editor.isActive('orderedList'),
    command: (editor) => editor.chain().focus().toggleOrderedList().run(),
  },
  {
    id: 'blockquote',
    label: 'Block Quote',
    icon: Quote,
    isActive: (editor) => editor.isActive('blockquote'),
    command: (editor) => editor.chain().focus().toggleBlockquote().run(),
  },
]

interface EditorBottomBarProps {
  editor: Editor
}

export function EditorBottomBar({ editor }: EditorBottomBarProps) {
  const [open, setOpen] = useState(false)
  const [activeBlock, setActiveBlock] = useState<BlockType>(blockTypes[0])
  const [wordCount, setWordCount] = useState(0)

  const updateActiveBlock = useCallback(() => {
    for (const block of blockTypes) {
      if (block.isActive(editor)) {
        setActiveBlock(block)
        return
      }
    }
    setActiveBlock(blockTypes[0])
  }, [editor])

  const updateWordCount = useCallback(() => {
    const text = editor.state.doc.textContent
    const words = text.trim() ? text.trim().split(/\s+/).length : 0
    setWordCount(words)
  }, [editor])

  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect -- initial sync from external editor state on subscribe */
    updateActiveBlock()
    updateWordCount()
    /* eslint-enable react-hooks/set-state-in-effect */

    editor.on('selectionUpdate', updateActiveBlock)
    editor.on('update', updateActiveBlock)
    editor.on('update', updateWordCount)

    return () => {
      editor.off('selectionUpdate', updateActiveBlock)
      editor.off('update', updateActiveBlock)
      editor.off('update', updateWordCount)
    }
  }, [editor, updateActiveBlock, updateWordCount])

  const handleSelect = (block: BlockType) => {
    block.command(editor)
    setOpen(false)
  }

  const ActiveIcon = activeBlock.icon

  return (
    <div className="absolute bottom-0 left-0 right-0 h-12 border-t border-border bg-white/90 backdrop-blur flex items-center justify-between px-4 z-20">
      {/* Left: spacer to keep block selector centered */}
      <div className="w-20" />

      {/* Center: Block Type Selector */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-2 text-xs font-medium">
            <ActiveIcon className="h-3.5 w-3.5" />
            {activeBlock.label}
            <ChevronsUpDown className="h-3 w-3 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-48 p-1" align="center" side="top" sideOffset={8}>
          {blockTypes.map((block) => {
            const Icon = block.icon
            const isActive = block.id === activeBlock.id
            return (
              <button
                key={block.id}
                onClick={() => handleSelect(block)}
                className={`flex items-center gap-2.5 w-full rounded-md px-2.5 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-accent text-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span className="flex-1 text-left">{block.label}</span>
                {isActive && <Check className="h-3.5 w-3.5 shrink-0" />}
              </button>
            )
          })}
        </PopoverContent>
      </Popover>

      {/* Right: Word count */}
      <span className="text-xs text-muted-foreground tabular-nums">
        {wordCount} {wordCount === 1 ? 'word' : 'words'}
      </span>
    </div>
  )
}
